import { useRef, useState, type RefObject } from 'react';
import {
  useDeleteHuddleBackground,
  useHuddleBackgrounds,
  useUploadHuddleBackground,
} from '../data/huddle-backgrounds';
import { NO_BACKGROUND, sameBackground, type VideoBackground } from '../huddle/blur';
import { useDismiss } from '../lib/dismiss';
import { apiBase } from '../lib/runtime';
import { Icon } from './Icon';

/**
 * HATTER-VALASZTO a kamerakephez: semmi, elmosas, vagy egy sajat kep.
 *
 * A sajat kepek itt helyben feltolthetok es torolhetok - nem kell hozza a
 * beallitasokba menni, hivas kozben jut eszebe az embernek.
 */
export function BackgroundPicker({
  value,
  onChange,
  triggerRef,
  onClose,
}: {
  value: VideoBackground;
  onChange: (background: VideoBackground) => void;
  triggerRef: RefObject<HTMLButtonElement | null>;
  onClose: () => void;
}) {
  const panelRef = useRef<HTMLDivElement | null>(null);
  useDismiss(panelRef, onClose, { ignore: [triggerRef], returnFocus: triggerRef });
  const fileRef = useRef<HTMLInputElement | null>(null);
  const { data: backgrounds = [], isLoading } = useHuddleBackgrounds();
  const upload = useUploadHuddleBackground();
  const remove = useDeleteHuddleBackground();
  const [error, setError] = useState<string | null>(null);

  const pickFile = async (file: File) => {
    setError(null);
    try {
      const background = await upload.mutateAsync(file);
      // A frissen feltoltott kepet azonnal hasznalni is akarja - ezert toltotte fel.
      onChange({ kind: 'image', id: background.id, url: background.url });
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'A feltöltés nem sikerült.');
    }
  };

  const deleteBackground = async (id: string) => {
    setError(null);
    try {
      await remove.mutateAsync(id);
      // A torolt kep nem maradhat a kamerakep mogott.
      if (value.kind === 'image' && value.id === id) onChange(NO_BACKGROUND);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'A törlés nem sikerült.');
    }
  };

  const tile = (selected: boolean) =>
    [
      'relative grid aspect-video w-full place-items-center overflow-hidden rounded-control text-xs',
      'transition-[box-shadow,background-color] duration-(--duration-quick)',
      selected
        ? 'ring-2 ring-accent ring-offset-2 ring-offset-surface-raised'
        : 'bg-surface-sunken text-ink-muted hover:bg-surface-hover hover:text-ink',
    ].join(' ');

  return (
    <div
      ref={panelRef}
      role="dialog"
      aria-label="Háttér a kamerakép mögött"
      className="absolute bottom-full left-3 z-30 mb-2 w-[22rem] max-w-[calc(100vw-1.5rem)] rounded-card border border-border bg-surface-raised p-3 shadow-modal"
    >
      <p className="mb-2 text-xs font-semibold text-ink-muted">Háttér</p>
      <ul className="grid grid-cols-3 gap-2">
        <li>
          <button
            type="button"
            aria-pressed={value.kind === 'none'}
            onClick={() => onChange(NO_BACKGROUND)}
            className={tile(value.kind === 'none')}
          >
            Nincs
          </button>
        </li>
        <li>
          <button
            type="button"
            aria-pressed={value.kind === 'blur'}
            onClick={() => onChange({ kind: 'blur' })}
            className={tile(value.kind === 'blur')}
          >
            <span className="flex flex-col items-center gap-1">
              <Icon name="rejt" />
              Elmosás
            </span>
          </button>
        </li>
        {backgrounds.map((background) => {
          const choice: VideoBackground = {
            kind: 'image',
            id: background.id,
            url: background.url,
          };
          const selected = sameBackground(value, choice);
          return (
            <li key={background.id} className="group relative">
              <button
                type="button"
                aria-pressed={selected}
                aria-label="Saját háttér"
                onClick={() => onChange(choice)}
                className={tile(selected)}
              >
                <img
                  src={apiBase() + background.url}
                  alt=""
                  loading="lazy"
                  className="absolute inset-0 size-full object-cover"
                />
              </button>
              <button
                type="button"
                aria-label="Háttér törlése"
                title="Háttér törlése"
                disabled={remove.isPending}
                onClick={() => void deleteBackground(background.id)}
                className="absolute right-1 top-1 grid size-5 place-items-center rounded-full bg-black/60 text-2xs text-white opacity-0 transition-opacity hover:bg-black/80 focus-visible:opacity-100 group-hover:opacity-100"
              >
                ×
              </button>
            </li>
          );
        })}
        <li>
          <button
            type="button"
            disabled={upload.isPending}
            onClick={() => fileRef.current?.click()}
            className={`${tile(false)} border border-dashed border-border-strong disabled:cursor-wait disabled:opacity-60`}
          >
            <span className="flex flex-col items-center gap-1">
              <span aria-hidden className="text-base leading-none">
                +
              </span>
              {upload.isPending ? 'Feltöltés…' : 'Saját kép'}
            </span>
          </button>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(event) => {
              const file = event.target.files?.[0];
              event.target.value = '';
              if (file) void pickFile(file);
            }}
          />
        </li>
      </ul>
      {isLoading && <p className="mt-2 text-2xs text-ink-faint">Hátterek betöltése…</p>}
      {!isLoading && backgrounds.length === 0 && (
        <p className="mt-2 text-2xs text-ink-faint">
          Tölts fel egy képet, és az kerül mögéd a kameraképen.
        </p>
      )}
      {error && (
        <p role="alert" className="mt-2 text-xs text-danger">
          {error}
        </p>
      )}
    </div>
  );
}
